/**
 * A planilha dos pedidos finalizados, como a tabela da seção os mostra.
 *
 * Papel: aqui ficam `CABECALHOS_DOS_FINALIZADOS` e `planilhaDosFinalizados`,
 * que transformam as linhas da tabela "Finalizados" na `Planilha` que o botão
 * "⇩ Exportar Excel" entrega a `exportarPlanilha`.
 *
 * Como usar: quem chama passa os pedidos que a tabela está mostrando, já
 * filtrados pela busca e na ordem em que estão na tela — a planilha não filtra
 * nem ordena, e sai igual à tabela no instante do clique. Cada célula sai da
 * mesma leitura que a coluna da tabela usa (`descricaoDoPedido`,
 * `residenciaDoPedido`, `fornecedorDoPedido`, `ROTULOS_CATEGORIA`,
 * `resultadoDoPedido`); escrever uma leitura própria aqui faria o arquivo
 * discordar da tela, como o pedido sem casa saindo com a residência em branco.
 *
 * A planilha tem as colunas de `CABECALHOS_DOS_FINALIZADOS`, que não são as da
 * tabela: o número vem primeiro, o motivo da reprovação entra numa coluna
 * própria, e o solicitante, que a tabela mostra, fecha a linha, depois do
 * valor. Uma coluna nova deverá entrar também no fim, para que quem já lê o
 * arquivo por coluna continue achando as outras no mesmo lugar.
 */
import { type PedidoCompra, totalDoPedido } from '@concierge/shared';

import { type Planilha } from '@/shared/lib/planilha';
import {
  descricaoDoPedido,
  fornecedorDoPedido,
  residenciaDoPedido,
  resultadoDoPedido,
} from './resumo';
import { ROTULOS_CATEGORIA } from './rotulos';

/** A primeira linha da planilha, na ordem de `planilhaDosFinalizados`. */
export const CABECALHOS_DOS_FINALIZADOS = [
  'Número',
  'Descrição',
  'Data',
  'Residência',
  'Fornecedor',
  'Categoria',
  'Aprovador',
  'Comprado por',
  'Resultado',
  'Motivo reprovação',
  'Valor',
  'Solicitante',
] as const;

/**
 * Monta a planilha dos pedidos finalizados.
 *
 * A data sai como data e o valor como número, o total dos itens pela mesma
 * `totalDoPedido` da coluna Valor; o que o pedido não tem — número em branco,
 * fornecedor, aprovador, comprador, motivo, solicitante em branco — sai como
 * célula vazia, e não como o travessão da tela.
 *
 * @param pedidos - Os pedidos que a tabela mostra, na ordem da tela.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, como `hojeEmIso` a dá, que
 *   entra no nome do arquivo.
 * @returns A planilha, com a aba "Finalizados" e o arquivo
 *   `pedidos-compra-finalizados-AAAA-MM-DD.xlsx`.
 */
export function planilhaDosFinalizados(
  pedidos: readonly PedidoCompra[],
  hojeIso: string,
): Planilha {
  return {
    nomeDoArquivo: `pedidos-compra-finalizados-${hojeIso}.xlsx`,
    abas: [
      {
        nome: 'Finalizados',
        cabecalhos: CABECALHOS_DOS_FINALIZADOS,
        linhas: pedidos.map((pedido) => [
          pedido.numero === '' ? null : pedido.numero,
          descricaoDoPedido(pedido),
          { data: pedido.data },
          residenciaDoPedido(pedido),
          fornecedorDoPedido(pedido),
          ROTULOS_CATEGORIA[pedido.categoria],
          pedido.aprovador,
          pedido.compradoPor,
          resultadoDoPedido(pedido),
          pedido.motivoReprovacao,
          totalDoPedido(pedido.itens),
          pedido.solicitante === '' ? null : pedido.solicitante,
        ]),
      },
    ],
  };
}
